import {useCallback, useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {useTranslation} from "react-i18next";
import i18n from "../i18n";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {PageHeader} from "../components/ui/PageHeader";
import {colors} from "../theme/tokens";
import {useAppDispatch, useAppSelector} from "../store/hooks";
import {fetchTenants, tenantsSelectors} from "../store/tenantsSlice";
import {getEmployees} from "../api/tenantDetailApi";
import type {Employee} from "../types/tenant";

type SupportEngineerRow = {
    id: string;
    alias: string;
    tenantId: string;
    tenantName: string;
};

type Order = "asc" | "desc";

export function SupportPage() {
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const tenants = useAppSelector(tenantsSelectors.selectAll);
    const tenantsStatus = useAppSelector((s) => s.tenants.status);

    const [supportEngineers, setSupportEngineers] = useState<
            (Employee & {isSupportEngineerForTenant: string})[]
    >([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [orderBy, setOrderBy] = useState<keyof SupportEngineerRow>("alias");
    const [order, setOrder] = useState<Order>("asc");

    useEffect(() => {
        document.title = t("SUPPORT_PAGE_TITLE");
    }, [t]);

    const tenantsById = useMemo(() => new Map(tenants.map((t) => [t.id, t])), [tenants]);

    useEffect(() => {
        if (tenantsStatus === "idle") void dispatch(fetchTenants());
    }, [dispatch, tenantsStatus]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getEmployees()
                .then((employees) => {
                    if (cancelled) return;
                    const filtered = employees.filter(
                            (e: Employee): e is Employee & {isSupportEngineerForTenant: string} =>
                                    Boolean(e.isSupportEngineerForTenant),
                    );
                    setSupportEngineers(filtered);
                })
                .catch((e) => {
                    if (!cancelled) {
                        setError(e instanceof Error ? e.message : i18n.t("SUPPORT_LOAD_ERROR"));
                        setSupportEngineers([]);
                    }
                })
                .finally(() => {
                    if (!cancelled) setLoading(false);
                });
        return () => {
            cancelled = true;
        };
    }, []);

    const rows = useMemo(
            () =>
                    supportEngineers.map((e) => ({
                        id: e.id,
                        alias: e.alias,
                        tenantId: e.isSupportEngineerForTenant,
                        tenantName: tenantsById.get(e.isSupportEngineerForTenant)?.name ?? "-",
                    })),
            [supportEngineers, tenantsById],
    );

    const sortedRows = useMemo(() => {
        return [...rows].sort((a, b) => {
            const aVal = a[orderBy];
            const bVal = b[orderBy];
            const cmp = String(aVal).localeCompare(String(bVal));
            return order === "asc" ? cmp : -cmp;
        });
    }, [rows, orderBy, order]);

    const handleSort = useCallback((column: keyof SupportEngineerRow) => {
        setOrderBy((prev) => {
            if (prev === column) setOrder((o) => (o === "asc" ? "desc" : "asc"));
            else setOrder("asc");
            return column;
        });
    }, []);

    const handleRowClick = useCallback(
            (row: SupportEngineerRow) => {
                navigate(`/tenants/${row.tenantId}`);
            },
            [navigate],
    );

    const sortHeader = (column: keyof SupportEngineerRow, label: string) => (
            <TableCell sortDirection={orderBy === column ? order : false}>
                <TableSortLabel active={orderBy === column} direction={orderBy === column ? order : "asc"} onClick={() => handleSort(column)}>
                    {label}
                </TableSortLabel>
            </TableCell>
    );

    return (
            <>
                <PageHeader title={t("NAV_SUPPORT")} subtitle={t("SUPPORT_SUBTITLE")} />
                {error && <Alert severity="error">{error}</Alert>}
                <Paper variant="outlined" sx={{overflow: "hidden"}}>
                    <TableContainer>
                        <Table aria-label={t("SUPPORT_TABLE_ARIA")}>
                            <TableHead>
                                <TableRow>
                                    {sortHeader("alias", t("SUPPORT_COLUMN_ALIAS"))}
                                    {sortHeader("tenantName", t("SUPPORT_COLUMN_TENANT"))}
                                    <TableCell sx={{width: 40}} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading && rows.length === 0 &&
                                        Array.from({length: 4}, (_, index) => (
                                                <TableRow key={index}>
                                                    <TableCell colSpan={3}><Skeleton height={24} /></TableCell>
                                                </TableRow>
                                        ))}
                                {!loading && rows.length === 0 && !error && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{py: 4, color: colors.muted}}>{t("SUPPORT_EMPTY")}</TableCell>
                                        </TableRow>
                                )}
                                {sortedRows.map((row) => (
                                        <TableRow
                                                key={row.id}
                                                hover
                                                tabIndex={0}
                                                onClick={() => handleRowClick(row)}
                                                onKeyDown={(event) => {
                                                    if (event.key === "Enter") handleRowClick(row);
                                                }}
                                                sx={{cursor: "pointer"}}
                                        >
                                            <TableCell>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1.25}}>
                                                    <Box
                                                            aria-hidden
                                                            sx={{width: 22, height: 22, borderRadius: "50%", backgroundColor: colors.navHover, color: colors.chrome, fontSize: "9.5px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}
                                                    >
                                                        {initialsOf(row.alias)}
                                                    </Box>
                                                    <Box component="span" sx={{color: colors.ink}}>{row.alias}</Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{color: colors.primary}}>{row.tenantName}</TableCell>
                                            <TableCell sx={{color: colors.faint, pl: 0}}><ChevronRightIcon sx={{fontSize: 18, display: "block"}} /></TableCell>
                                        </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
                <Typography variant="body2" sx={{color: colors.faint}}>{t("SUPPORT_HINT")}</Typography>
            </>
    );
}

function initialsOf(alias: string): string {
    return alias.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join("");
}
